import { useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import {
  createEventCalendar,
  deleteEventCalendar,
  getCalendarEvents,
  getCalendarIdCalendar,
  updateEventCalendar,
  EnumArmingEventType,
  CalendarEventDto,
} from '@/features/Calendar/calendarApi';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useParams } from 'react-router-dom';
import { format, parse, startOfWeek, getDay, isBefore, isSameDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSite } from '@/hooks/siteHooks';
import { getTimeZoneName, useCurrentTime } from '@/lib/time';

interface CalanderProps {
  projectId: string;
  customerId?: string;
}

interface CalEvent {
  id: string;
  title: string;
  description: string;
  start: Date;
  end: Date;
  armingEventType: EnumArmingEventType;
  isEnabled: boolean;
}

const baseEventFormSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  start: z.date({ required_error: 'Start date is required' }),
  end: z.date({ required_error: 'End date is required' }),
  armingEventType: z.nativeEnum(EnumArmingEventType),
});

const normalizeDateToProjectUtc = (date: Date, timeZone?: string) => {
  if (!timeZone) {
    return date;
  }

  return fromZonedTime(date, timeZone);
};

/**
 * Converts a local date to naive UTC while preserving the same clock time.
 * e.g., 12:00 PM (local) -> 12:00 PM UTC (not converted, just stripped of timezone)
 * 
 * The dates in the form are local Date objects representing the project timezone time.
 * We save them as UTC with the same clock time values.
 */
const toNaiveUtc = (date: Date) => {
  return new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  ));
};

export const createEventFormSchema = (timeZone?: string) => {
  const getNowInProjectUtc = () => new Date();

  return baseEventFormSchema
    .refine((data) => !isBefore(normalizeDateToProjectUtc(data.start, timeZone), getNowInProjectUtc()), {
      message: 'Start date must be in the future',
      path: ['start'],
    })
    .refine((data) => !isBefore(normalizeDateToProjectUtc(data.end, timeZone), getNowInProjectUtc()), {
      message: 'End date must be in the future',
      path: ['end'],
    })
    .refine(
      (data) =>
        normalizeDateToProjectUtc(data.start, timeZone) <= normalizeDateToProjectUtc(data.end, timeZone),
      {
        message: 'End date must be after start date',
        path: ['end'],
      }
    );
};

type FormValues = z.infer<typeof baseEventFormSchema>;

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {},
});

export function SiteCalendar({ projectId, customerId }: CalanderProps) {
  const params = useParams<{ customerId?: string }>();
  const resolvedCustomerId = customerId || params.customerId;
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [existingEvent, setExistingEvent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [calendarId, setCalendarId] = useState<string>();
  const [selectedEventIsEnabled, setSelectedEventIsEnabled] = useState(true);

  const { data: site } = useSite({
    siteId: projectId,
    companyId: resolvedCustomerId || '',
    enabled: Boolean(projectId && resolvedCustomerId),
  });

  const projectTimeZone = site?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentTime = useCurrentTime(1000); // Update every second
  
  // Log timezone for debugging
  //console.log('SiteCalendar timezone:', projectTimeZone, '| site?.timeZone:', site?.timeZone, '| customerId:', resolvedCustomerId, '| projectId:', projectId);

  // Get current time in project timezone
  const projectNow = toZonedTime(currentTime, projectTimeZone);
  const projectTimeString = formatInTimeZone(currentTime, projectTimeZone, 'h:mm:ss a');
  const projectDateString = formatInTimeZone(currentTime, projectTimeZone, 'EEEE, MMMM d, yyyy');
  const timeZoneName = getTimeZoneName(projectTimeZone);

  const formSchema = useMemo(() => createEventFormSchema(projectTimeZone), [projectTimeZone]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      start: undefined,
      end: undefined,
      armingEventType: EnumArmingEventType.Armed,
    },
  });

  const toProjectZonedTime = (date: Date) =>
    projectTimeZone ? toZonedTime(date, projectTimeZone) : date;

  const formatProjectTime = (date: Date, pattern: string) =>
    projectTimeZone ? formatInTimeZone(date, projectTimeZone, pattern) : format(date, pattern);

  const getProjectCalEvents = async () => {
    let res = await getCalendarIdCalendar(projectId);

    if (!res.calendarId) {
      return;
    }

    setCalendarId(res.calendarId);
    const events = await getCalendarEvents(res.calendarId);

    if (!events.calendarEvents) {
      return;
    }

      let calEvents = events.calendarEvents
        .map((event) => {
          return {
            id: event.id!,
            title: event.name! + ' - ' + event.armingEventType!,
            description: event.description!,
            start: new Date(event.startDate!),
            end: new Date(event.endDate!),
            armingEventType: event.armingEventType!,
            isEnabled: event.isEnabled!,
          };
        });

    setEvents(calEvents);
  };

  useEffect(() => {
    if (!projectId) {
      return;
    }
    getProjectCalEvents();
  }, [projectId]);

  if (!projectId) {
    return null;
  }

  const slotSelected = (slotInfo: any) => {
    // slotInfo.start is already in project timezone since the calendar displays in project timezone
    // Just use it directly without converting again
    let start = new Date(slotInfo.start);
    start.setHours(0);
    start.setMinutes(1);
    let end = new Date(slotInfo.start);
    end.setHours(23);
    end.setMinutes(59);

    form.reset({
      id: '',
      title: '',
      description: '',
      start: start,
      end: end,
      armingEventType: EnumArmingEventType.Armed,
    });
    setSelectedEventIsEnabled(true);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const eventSelected = (event: CalEvent) => {
    // Find the original event from state (not the display-transformed one)
    const originalEvent = events.find(e => e.id === event.id);
    if (!originalEvent) return;
    
    setSelectedEventIsEnabled(originalEvent.isEnabled);
    setExistingEvent(true);
    // Convert naive UTC to local Date for form display
    const startLocal = new Date(
      originalEvent.start.getUTCFullYear(),
      originalEvent.start.getUTCMonth(),
      originalEvent.start.getUTCDate(),
      originalEvent.start.getUTCHours(),
      originalEvent.start.getUTCMinutes()
    );
    const endLocal = new Date(
      originalEvent.end.getUTCFullYear(),
      originalEvent.end.getUTCMonth(),
      originalEvent.end.getUTCDate(),
      originalEvent.end.getUTCHours(),
      originalEvent.end.getUTCMinutes()
    );
    form.reset({
      id: originalEvent.id,
      title: originalEvent.title.split(' - ')[0],
      description: originalEvent.description,
      start: startLocal,
      end: endLocal,
      armingEventType: originalEvent.armingEventType,
    });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!calendarId || !form.getValues('id')) return;

    try {
      const values = form.getValues();
      await deleteEventCalendar(calendarId, {
        id: values.id,
        name: values.title,
        description: values.description,
        startDate: values.start,
        endDate: values.end,
        armingEventType: values.armingEventType,
        isPreset: true,
        isEnabled: true,
      });

      await getProjectCalEvents(); // Refresh events
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
    setExistingEvent(false);
  };

  const handleEditSave = async (values: FormValues) => {
    if (!calendarId) return;

    try {
      // Convert to naive UTC - preserves clock time but strips timezone
      const response = await updateEventCalendar(calendarId, {
        id: values.id,
        name: values.title,
        description: values.description,
        startDate: toNaiveUtc(values.start),
        endDate: toNaiveUtc(values.end),
        isEnabled: true,
        armingEventType: values.armingEventType,
      });
      if (response.success) {
        await getProjectCalEvents(); // Refresh events
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to update event:', error);
    }
    setExistingEvent(false);
  };

  const onSubmit = async (data: FormValues) => {
    if (!calendarId) {
      return;
    }

    // Convert to naive UTC - preserves clock time but strips timezone
    // e.g., 12:00 PM Auckland -> 12:00 PM UTC
    const eventData: CalendarEventDto = {
      name: data.title,
      description: data.description,
      startDate: toNaiveUtc(data.start),
      endDate: toNaiveUtc(data.end),
      armingEventType: data.armingEventType,
      isPreset: true,
      isEnabled: true,
    };

    try {
      const result = await createEventCalendar(calendarId, eventData);
      if (result.success) {
        await getProjectCalEvents(); // Refresh events
        setIsModalOpen(false);
        form.reset();
      }
    } catch (error) {
      console.error('Failed to create event:', error);
    }
  };

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {form.getValues('id')
                ? isEditing
                  ? 'Edit Event'
                  : 'View Event'
                : 'Create New Event'}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-6 px-4 py-2"
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={form.getValues('id') !== '' && !isEditing} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={form.getValues('id') !== '' && !isEditing} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="armingEventType"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Event Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={form.getValues('id') !== '' && !isEditing}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(EnumArmingEventType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <DateTimePicker
                        value={field.value}
                        onChange={field.onChange}
                        disabled={form.getValues('id') !== '' && !isEditing}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <DateTimePicker
                        value={field.value}
                        onChange={field.onChange}
                        disabled={form.getValues('id') !== '' && !isEditing}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="px-4 py-4">
                <div className="flex w-full flex-col gap-2">
                  {/* Show Cancel button when creating new event or in edit mode */}
                  {(!existingEvent || isEditing) && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        if (isEditing) {
                          setIsEditing(false);
                        } else {
                          setIsModalOpen(false);
                        }
                      }}
                    >
                      {isEditing ? 'Cancel' : 'Close'}
                    </Button>
                  )}

                  {form.getValues('id') ? (
                    isEditing ? (
                      <Button
                        type="button"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          form.handleSubmit(handleEditSave)(e);
                        }}
                      >
                        Save Changes
                      </Button>
                    ) : selectedEventIsEnabled ? (
                      <Button
                        type="button"
                        className="w-full"
                        onClick={() => {
                          if (!selectedEventIsEnabled) return;
                          setIsEditing(true);
                        }}
                      >
                        Edit Event
                      </Button>
                    ) : null
                  ) : (
                    <Button type="submit" className="w-full">
                      Create Event
                    </Button>
                  )}

                  {/* Show Delete button only when viewing (not editing) an existing event */}
                  {existingEvent && !isEditing && (
                    <Button
                      type="button"
                      variant="destructive"
                      className="w-full"
                      onClick={handleDeleteEvent}
                      disabled={!selectedEventIsEnabled}
                    >
                      Delete Event
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <div className="rounded-md border bg-[#111]">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-xl font-semibold">Event Schedule</h2>
          <div className="text-right">
            <div className="text-xs font-medium text-gray-400">Site Timezone</div>
            <div className="text-lg font-medium text-white">{projectTimeString}</div>
            <div className="text-sm text-gray-400">{projectDateString}</div>
            <div className="text-xs font-medium text-lime-500">{timeZoneName}</div>
          </div>
        </div>
        <div className="flex items-start justify-between p-4 pb-0">
          <p className="text-gray-400">Specify when to disarm your cameras</p>
          <div className="flex gap-4 text-gray-400">
            <div className="flex items-center gap-1 text-xs" data-testid="legend-armed">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#84cc16' }}
              />
              <span>Armed</span>
            </div>
            <div className="flex items-center gap-1 text-xs" data-testid="legend-disarmed">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#991b1b' }}
              />
              <span>Disarmed</span>
            </div>
          </div>
        </div>
        <Calendar
          localizer={localizer}
          events={events.map((event) => ({
            ...event,
            // Events are stored as naive UTC (clock time matches project timezone)
            // So we just need to treat the UTC values as local display values
            start: new Date(
              event.start.getUTCFullYear(),
              event.start.getUTCMonth(),
              event.start.getUTCDate(),
              event.start.getUTCHours(),
              event.start.getUTCMinutes()
            ),
            end: new Date(
              event.end.getUTCFullYear(),
              event.end.getUTCMonth(),
              event.end.getUTCDate(),
              event.end.getUTCHours(),
              event.end.getUTCMinutes()
            ),
          }))}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 'calc(100vh - 16rem)' }}
          onSelectEvent={eventSelected}
          onSelectSlot={slotSelected}
          selectable
          className="p-4"
            formats={{
              timeGutterFormat: (date: Date) => formatProjectTime(date, 'HH:mm'),
              eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
                `${formatProjectTime(start, 'HH:mm')} - ${formatProjectTime(end, 'HH:mm')}`,
              dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
                `${formatProjectTime(start, 'MMM dd')} - ${formatProjectTime(end, 'MMM dd')}`,
            }}
          eventPropGetter={(event) => ({
            className: event.isEnabled ? 'cursor-pointer' : '',
            style: {
              backgroundColor: !event.isEnabled
                ? '#bcbfb8'
                : event.armingEventType === EnumArmingEventType.Armed
                  ? '#84cc16'
                  : '#991b1b',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            },
          })}
          dayPropGetter={(date) => {
            const isToday = isSameDay(date, projectNow);
            return {
              className: 'cursor-pointer hover:bg-gray-50 transition-colors',
              style: isToday ? { backgroundColor: 'rgba(132, 204, 22, 0.15)' } : undefined,
            };
          }}
        />
      </div>
    </>
  );
}

